import React from "react";
import {
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableBody,
  Box,
  Typography,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { useTheme } from "@emotion/react";
import StyledTableCell from "../Common/styledTableCell";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { CustomTooltip } from "../../Utils/handler";

// Mapping of column headers to sorting keys
const SORTABLE_COLUMNS = {
  "PPSK ID": "isPpskId",
  "Submitted Date": "isSubmittedDate",
  "Updated Date": "isUpdatedDate",
  "Verified By": "isVerifyBy",
  "Remaining Renew Days": "isRenewDays",
  "Payment Date": "isPaymentDate",
  "Start Date": "isSubmittedDate", // Uses same sort as submitted
  "End Date": "isUpdatedDate", // Uses same sort as updated
  "Name": "isName",
  "Mobile No": "isMobile",
  "Member Name": "isName",
  "Mobile": "isMobile",
  "Sub Status": "isSubStatus",
  "Days Left": "isRenewDays",
  "Days Inactive": "isSubmittedDate",
  "Days Since Update": "isUpdatedDate",
  "Days Pending": "isUpdatedDate",
  "Registered On": "isSubmittedDate",
  "Last Active": "isUpdatedDate",
  "Submitted On": "isSubmittedDate",
  "Rejected On": "isUpdatedDate",
  "Marked On": "isUpdatedDate",
};

const useStyles = makeStyles()((theme) => ({
  main: {
    padding: 0,
    "thead th": {
      color: theme.palette.primary.main,
      fontSize: "15px",
      padding: "5px",
    },
    tbody: {
      "tr:first-of-type td": {
        backgroundColor: "#FFFFFF",
        padding: "4px 0 12px 0",
      },
      "tr td": {
        backgroundColor: "#F8F8F8",
        borderBottom: "none",
        margin: 8,
      },
      "td:first-of-type": {
        borderRadius: "8px 0 0 8px",
      },
      "td:last-of-type": {
        borderRadius: "0 8px 8px 0",
      },
    },
  },
}));

const CommonTable = ({
  headerList,
  children,
  minWidth,
  align,
  sx,
  sorting,
  setSorting,
  backgroundColor,
  size,
  isNotSorting
}) => {
  const { classes } = useStyles();
  const theme = useTheme();

  // Check if a column is sortable
  const isSortable = (columnName) => {
    return SORTABLE_COLUMNS[columnName] && !isNotSorting && setSorting;
  };

  // Get current sort state for a column
  const getSortState = (columnName) => {
    const sortKey = SORTABLE_COLUMNS[columnName];
    if (!sortKey || !sorting) return null;
    return sorting[sortKey];
  };

  // Handle sort click
  const handleSort = (columnName) => {
    const sortKey = SORTABLE_COLUMNS[columnName];
    if (!sortKey) return;

    const currentValue = sorting?.[sortKey];
    const newValue = currentValue === "0" ? "1" : currentValue === "1" ? "0" : "0";

    // Reset all sorting and set only the clicked column
    const newSorting = {
      isPpskId: "",
      isSubmittedDate: "",
      isUpdatedDate: "",
      isRenewDays: "",
      isVerifyBy: "",
      isPaymentDate: "",
      isName: "",
      isMobile: "",
      isSubStatus: "",
    };
    newSorting[sortKey] = newValue;
    setSorting(newSorting);
  };

  // Get sort icon based on state
  const getSortIcon = (columnName) => {
    const state = getSortState(columnName);
    if (state === "0") {
      return <ArrowDownwardIcon sx={{ fontSize: "1rem", ml: 0.5, color: "#fff" }} />;
    } else if (state === "1") {
      return <ArrowUpwardIcon sx={{ fontSize: "1rem", ml: 0.5, color: "#fff" }} />;
    }
    return <SwapVertIcon sx={{ fontSize: "1.1rem", ml: 0.3, opacity: 0.7 }} />;
  };

  return (
    <TableContainer
      sx={{ ...sx, width: "100%", overflowX: "auto", borderRadius: "10px" }}
      elevation={0}
    // className={classes.main}
    >
      <Box>
        <Table
          sx={{ minWidth: minWidth || 650 }}
          size={size || "small"}
          aria-label="order table"
        >
          <TableHead
            sx={{
              backgroundColor: backgroundColor || theme?.palette?.primary?.main,
              "& th": { color: "#fff" },
            }}
          >
            <TableRow>
              {headerList &&
                headerList.map((item, index) => (
                  <StyledTableCell
                    key={index}
                    sx={{ padding: "8px" }}
                    align={align || "center"}
                  >
                    <Typography
                      display={"flex"}
                      sx={{
                        color: theme?.palette?.bgLightWhite?.main,
                        fontSize: 14,
                        lineHeight: "1.2rem",
                        cursor: isSortable(item) ? "pointer" : "default",
                        userSelect: "none",
                        "&:hover": isSortable(item) ? { opacity: 0.85 } : {},
                      }}
                      alignItems={"center"}
                      justifyContent={"center"}
                      onClick={() => isSortable(item) && handleSort(item)}
                    >
                      {item}
                      {isSortable(item) && (
                        <CustomTooltip
                          title={
                            getSortState(item) === "0" 
                              ? "Sorted: Newest First (Click for Oldest)" 
                              : getSortState(item) === "1" 
                                ? "Sorted: Oldest First (Click for Newest)" 
                                : "Click to Sort"
                          }
                        >
                          <Box component="span" sx={{ display: "flex", alignItems: "center" }}>
                            {getSortIcon(item)}
                          </Box>
                        </CustomTooltip>
                      )}
                    </Typography>
                  </StyledTableCell>
                ))}
            </TableRow>
          </TableHead>
          <TableBody sx={{ border: `1px solid lightgrey` }}>
            {children}
          </TableBody>
        </Table>
      </Box>
    </TableContainer>
  );
};

export default CommonTable;
